import React, { useEffect, useRef } from 'react';

import { useCustomerFooterRef } from 'components/customer/customer_footer_ref_context';
import useDevice from 'components/hooks/use_device';
import { Device } from 'scripts/lib/device_detector';

const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

/**
 * Prevents the iOS virtual keyboard from dismissing when the composer
 * remounts (e.g. after sending a message). Uses a hidden input to catch
 * focus any time it leaves an editable element in the footer.
 * Only active on touch devices (iPhone, iPad).
 */
export default function MobileKeyboardKeepAlive() {
  const ref = useRef(null);
  const { footerNode } = useCustomerFooterRef();
  const isPhone = useDevice() === Device.Phone;

  useEffect(() => {
    if (!isPhone || !isTouchDevice() || !footerNode) return undefined;

    function handleFocusOut() {
      // Immediately focus the hidden input so the keyboard doesn't have
      // time to start its dismiss animation. setTimeout(0) is too slow —
      // we need this synchronous in the focusout handler.
      if (ref.current) {
        ref.current.focus({ preventScroll: true });
      }
    }

    footerNode.addEventListener('focusout', handleFocusOut);
    return () => footerNode.removeEventListener('focusout', handleFocusOut);
  }, [isPhone, footerNode]);

  if (!isPhone || !isTouchDevice()) return null;

  return (
    <input
      aria-hidden="true"
      readOnly
      ref={ref}
      style={{ height: 0, left: -9999, opacity: 0, position: 'fixed', top: -9999, width: 0 }}
      tabIndex={-1}
    />
  );
}
